import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/modules/auth/store/auth'
import { fetchProfile } from '@/modules/auth/repository/authRepository'
import { createNavigationGuard } from './navigationGuard'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/modules/auth/ui/login/LoginView.vue'),
      meta: { public: true },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/modules/auth/ui/recovery/ForgotPasswordView.vue'),
      meta: { public: true },
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/modules/auth/ui/recovery/ResetPasswordView.vue'),
      meta: { public: true },
    },
    {
      path: '/platform/login',
      name: 'platform-login',
      component: () => import('@/modules/auth/ui/login/LoginView.vue'),
      props: { platform: true },
      meta: { public: true },
    },
    {
      path: '/',
      component: () => import('@/layouts/AppLayout.vue'),
      children: [
        {
          path: '',
          redirect: { name: 'dashboard' },
        },
        {
          path: 'dashboard',
          name: 'dashboard',
          component: () => import('@/modules/reports/ui/dashboard/BusinessStateDashboard.vue'),
          meta: { permissions: ['reports.view'] },
        },
        {
          path: 'customers',
          name: 'customers',
          component: () => import('@/modules/customers/ui/list/CustomersList.vue'),
          meta: { permissions: ['customers.view'] },
          children: [
            {
              path: ':customerId',
              name: 'customer-detail',
              component: () => import('@/modules/customers/ui/detail/CustomerDetailModal.vue'),
              meta: { permissions: ['customers.view'] },
            },
          ],
        },
        {
          path: 'collections',
          name: 'collections-due',
          component: () => import('@/modules/collections/ui/list/CollectionsDueView.vue'),
          meta: { permissions: ['loans.view'] },
        },
        {
          path: 'my-payments',
          name: 'my-payments',
          component: () => import('@/modules/payments/ui/mine/MyPaymentsView.vue'),
          meta: { permissions: ['payments.view'] },
        },
        {
          path: 'loans',
          name: 'loans',
          component: () => import('@/modules/loans/ui/list/LoansList.vue'),
          meta: { permissions: ['loans.view'] },
        },
        {
          path: 'loans/:loanId',
          name: 'loan-detail',
          component: () => import('@/modules/loans/ui/detail/LoanDetailView.vue'),
          meta: { permissions: ['loans.view'] },
        },
        {
          path: 'loan-requests',
          name: 'loan-requests',
          component: () => import('@/modules/loanRequests/ui/queue/LoanRequestQueueView.vue'),
          meta: {
            permissions: ['loanRequests.approve'],
            fallbackRouteName: 'my-loan-requests',
          },
        },
        {
          path: 'loan-requests/mine',
          name: 'my-loan-requests',
          component: () => import('@/modules/loanRequests/ui/mine/MyLoanRequestsView.vue'),
          meta: { permissions: ['loanRequests.create'] },
        },
        {
          path: 'accounting',
          name: 'accounting',
          redirect: { name: 'finance-overview' },
        },
        {
          path: 'finance/overview',
          name: 'finance-overview',
          component: () => import('@/modules/accounting/ui/pages/FinanceOverviewPage.vue'),
          meta: { permissions: ['reports.view'] },
        },
        {
          path: 'finance/chart',
          name: 'finance-chart',
          component: () => import('@/modules/accounting/ui/pages/FinanceChartPage.vue'),
          meta: { permissions: ['reports.view'] },
        },
        {
          path: 'finance/transactions',
          name: 'finance-transactions',
          component: () => import('@/modules/accounting/ui/pages/FinanceTransactionsPage.vue'),
          meta: { permissions: ['reports.view'] },
        },
        {
          path: 'finance/expenses',
          name: 'finance-expenses',
          component: () => import('@/modules/accounting/ui/pages/FinanceExpensesPage.vue'),
          meta: { permissions: ['reports.view'] },
        },
        {
          path: 'finance/impairment',
          name: 'finance-impairment',
          component: () => import('@/modules/accounting/ui/pages/FinanceImpairmentPage.vue'),
          meta: { permissions: ['reports.view'] },
        },
        {
          path: 'finance/period-close',
          name: 'finance-period-close',
          component: () => import('@/modules/accounting/ui/pages/FinancePeriodClosePage.vue'),
          meta: { permissions: ['reports.view'] },
        },
        {
          path: 'finance/posting-rules',
          name: 'finance-posting-rules',
          component: () => import('@/modules/accounting/ui/pages/FinancePostingRulesPage.vue'),
          meta: { permissions: ['reports.view'] },
        },
        {
          path: 'cash-close',
          name: 'cash-close',
          component: () => import('@/modules/treasury/ui/close/CashCloseView.vue'),
          meta: { permissions: ['settings.manage'] },
        },
        {
          path: 'commissions',
          name: 'commissions',
          component: () => import('@/modules/commissions/ui/CommissionsView.vue'),
          meta: { permissions: ['settings.manage'] },
        },
        {
          path: 'loan-tables',
          name: 'loan-tables',
          component: () => import('@/modules/loanTables/ui/list/LoanTablesView.vue'),
          meta: { permissions: ['loanTables.manage'] },
        },
        {
          path: 'routes',
          name: 'routes',
          component: () => import('@/modules/routes/ui/list/RoutesView.vue'),
          meta: { permissions: ['routes.manage'] },
          children: [
            {
              path: ':routeId',
              name: 'route-detail',
              component: () => import('@/modules/routes/ui/detail/RouteDetailModal.vue'),
              meta: { permissions: ['routes.manage'] },
            },
          ],
        },
        {
          path: 'members',
          name: 'members',
          component: () => import('@/modules/team/ui/list/TeamView.vue'),
          meta: { permissions: ['users.manage'] },
        },
        {
          path: 'payment-review',
          name: 'payment-review',
          component: () =>
            import('@/modules/paymentReview/ui/queue/PaymentReviewQueueView.vue'),
          meta: { permissions: ['payments.review'] },
        },
        {
          path: 'verify-receipt',
          name: 'verify-receipt',
          component: () => import('@/modules/payments/ui/lookup/VerifyReceiptView.vue'),
          meta: { permissions: ['payments.view'] },
        },
        {
          path: 'import',
          name: 'import-backup',
          component: () => import('@/modules/migration/ui/import/ImportBackupView.vue'),
          meta: { permissions: ['migration.import'] },
        },
        {
          path: 'settings',
          name: 'settings',
          component: () => import('@/modules/settings/ui/SettingsView.vue'),
        },
        {
          path: 'forbidden',
          name: 'forbidden',
          component: () => import('@/modules/auth/ui/forbidden/ForbiddenView.vue'),
        },
      ],
    },
    {
      path: '/platform',
      component: () => import('@/layouts/PlatformLayout.vue'),
      meta: { platform: true },
      children: [
        {
          path: '',
          name: 'platform',
          component: () => import('@/modules/platform/ui/overview/PlatformOverview.vue'),
        },
        {
          path: 'plans',
          name: 'platform-plans',
          component: () => import('@/modules/platform/ui/plans/PlansView.vue'),
        },
        {
          path: 'tenants',
          name: 'platform-tenants',
          component: () => import('@/modules/platform/ui/tenants/TenantsView.vue'),
        },
        {
          path: 'accounts',
          name: 'platform-accounts',
          component: () => import('@/modules/platform/ui/accounts/AccountsView.vue'),
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: { name: 'dashboard' },
    },
  ],
})

router.beforeEach(
  createNavigationGuard({
    getSession: () => useAuthStore(),
    fetchProfile,
  }),
)

export default router
